import React from 'react';
import { DialogProviderContext } from './DialogProviderContext';

interface ModalData {
    component: React.ComponentType<Record<string, unknown>>;
    open: boolean;
    onClose: () => void;
    data: Record<string, unknown>;
}

const DialogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [dialogs, setDialogs] = React.useState<Record<string, ModalData>>({});
    const activeElementRef = React.useRef<HTMLElement | null>();

    const removeModal = React.useCallback((modalKey: string) => {
        setDialogs(prevConfig => {
            const configs = { ...prevConfig };
            delete configs[modalKey];
            return configs;
        });
    }, []);

    const hideModal = React.useCallback(
        (modalKey: string, onClose?: () => void) => {
            setDialogs(prevConfig => {
                // edge case condition, when somebody is calling hideModal when modal is already hidden
                if (!prevConfig[modalKey]) return prevConfig;
                return { ...prevConfig, [modalKey]: { ...prevConfig[modalKey], open: false } };
            });

            // don't forget about modal fadeOut animation duration, currently none
            removeModal(modalKey);

            // focus previous active button
            if (activeElementRef.current) {
                requestAnimationFrame(() => {
                    activeElementRef.current?.focus();
                });
            }

            if (onClose) {
                onClose();
            }
        },
        [removeModal]
    );
    const showModal = React.useCallback(
        (modalKey, component, modalData, onClose) => {
            // store active button, so we can focus it when we close modal
            if (document.activeElement?.tagName === 'BUTTON') {
                activeElementRef.current = document.activeElement as HTMLElement;
            }
            setDialogs(prevConfig => ({
                ...prevConfig,
                [modalKey]: {
                    open: true,
                    component,
                    data: modalData,
                    onClose: () => {
                        hideModal(modalKey);
                        modalData.onClose?.();
                        onClose?.();
                    },
                },
            }));
        },
        [hideModal]
    );

    const contextValue = React.useMemo(
        () => ({
            showModal,
            hideModal,
            removeModal,
        }),
        [hideModal, showModal, removeModal]
    );

    return (
        <DialogProviderContext.Provider value={contextValue}>
            {children}
            {Object.keys(dialogs).map(modalKey => {
                const { component: Component, open, data, onClose } = dialogs[modalKey];

                return <Component key={modalKey} open={open} {...data} onClose={onClose} />;
            })}
        </DialogProviderContext.Provider>
    );
};

export default DialogProvider;
